import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { MemberSelectorPurpose, ScheduleListItem } from '../appService';
import {
  getAdjacentScheduleMonth,
  getScheduleMonthDateWindow,
  getScheduleStatusBoundaryRefreshMs
} from '../scheduleView';
import type { WorkspaceContextValue } from './appTypes';
import { useAppRuntime, useContextResetController, useWorkspaceContext } from './context';

export const APP_BOOTSTRAP_QUERY_KEY = ['app', 'bootstrap'] as const;

export function useBootstrapQuery() {
  const { service } = useAppRuntime();
  return useQuery({
    queryKey: APP_BOOTSTRAP_QUERY_KEY,
    queryFn: () => service.getViewModel()
  });
}

export function useAssignmentListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: assignmentListQueryKey(workspaceContext),
    queryFn: () => service.listAssignments({ workspaceContext })
  });
}

export function useAssignmentDetailQuery(assignmentId: string) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: assignmentDetailQueryKey(workspaceContext, assignmentId),
    queryFn: () => service.getAssignmentDetail({ workspaceContext, assignmentId })
  });
}

export function useMemberDisplayListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: memberDisplayListQueryKey(workspaceContext),
    queryFn: () => service.listMemberDisplayItems({ workspaceContext })
  });
}

export function useMemberGroupSummaryListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: memberGroupSummaryListQueryKey(workspaceContext),
    queryFn: () => service.listMemberGroupSummaries({ workspaceContext })
  });
}

export function useMemberGroupDetailQuery(memberGroupId: string, enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: memberGroupDetailQueryKey(workspaceContext, memberGroupId),
    queryFn: () => service.getMemberGroupDetail({ workspaceContext, memberGroupId }),
    enabled
  });
}

export function usePermissionGroupListQuery(enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: permissionGroupListQueryKey(workspaceContext),
    queryFn: () => service.listPermissionGroups({ workspaceContext }),
    enabled
  });
}

export function usePermissionGroupDetailQuery(permissionGroupId: string, enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: permissionGroupDetailQueryKey(workspaceContext, permissionGroupId),
    queryFn: () => service.getPermissionGroupDetail({ workspaceContext, permissionGroupId }),
    enabled
  });
}

export function useMemberSelectorItemsQuery(purpose: MemberSelectorPurpose, enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: memberSelectorQueryKey(workspaceContext, purpose),
    queryFn: () => service.listMemberSelectorItems({ workspaceContext, purpose }),
    enabled
  });
}

export function useInvitationListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: invitationListQueryKey(workspaceContext),
    queryFn: () => service.listInvitations({ workspaceContext })
  });
}

export function useApprovalListQuery(enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: approvalListQueryKey(workspaceContext),
    queryFn: () => service.listApprovals({ workspaceContext }),
    enabled
  });
}

export function useApprovalDetailQuery(joinRequestId: string, enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: approvalDetailQueryKey(workspaceContext, joinRequestId),
    queryFn: () => service.getApprovalDetail({ workspaceContext, joinRequestId }),
    enabled
  });
}

export function useAttendanceCategoryListQuery(enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: attendanceCategoryListQueryKey(workspaceContext),
    queryFn: () => service.listAttendanceCategories({ workspaceContext }),
    enabled
  });
}

export function useScheduleMonthQuery(monthKey: string) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const window = getScheduleMonthDateWindow(monthKey);

  return useQuery({
    queryKey: scheduleMonthQueryKey(workspaceContext, monthKey),
    queryFn: () => service.listSchedules({ workspaceContext, ...window }),
    staleTime: (query) => getScheduleMonthStaleTime(query.state.data),
    refetchInterval: (query) => getScheduleMonthRefetchInterval(query.state.data)
  });
}

export function useScheduleUpcomingQuery(limit: number) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: scheduleUpcomingQueryKey(workspaceContext, limit),
    queryFn: () => service.listUpcomingSchedules({ workspaceContext, limit }),
    staleTime: (query) => getScheduleMonthStaleTime(query.state.data),
    refetchInterval: (query) => getScheduleMonthRefetchInterval(query.state.data)
  });
}

export function useScheduleHomeUpcomingQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: scheduleHomeUpcomingQueryKey(workspaceContext),
    queryFn: () => service.getHomeUpcomingSchedule({ workspaceContext }),
    staleTime: (query) => getScheduleMonthStaleTime(query.state.data ? [query.state.data] : query.state.data),
    refetchInterval: (query) =>
      getScheduleMonthRefetchInterval(query.state.data ? [query.state.data] : query.state.data)
  });
}

export function useScheduleDetailQuery(scheduleId: string) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: scheduleDetailQueryKey(workspaceContext, scheduleId),
    queryFn: () => service.getScheduleDetail({ workspaceContext, scheduleId })
  });
}

export function useResourcesRootQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: resourcesRootQueryKey(workspaceContext),
    queryFn: () => service.getResourcesRoot({ workspaceContext })
  });
}

export function useSongListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: songListQueryKey(workspaceContext),
    queryFn: () => service.listSongs({ workspaceContext })
  });
}

export function useSongDetailQuery(songId: string, folderId: string | null = null) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: songDetailQueryKey(workspaceContext, songId, folderId),
    queryFn: () => service.getSongDetail({ workspaceContext, songId, folderId })
  });
}

export function useNotificationListQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: notificationListQueryKey(workspaceContext),
    queryFn: () => service.listNotifications({ workspaceContext })
  });
}

export function useUnreadNotificationStateQuery() {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: unreadNotificationStateQueryKey(workspaceContext),
    queryFn: () => service.getUnreadNotificationState({ workspaceContext })
  });
}

export function useScheduleEditOptionsQuery(enabled = true) {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();

  return useQuery({
    queryKey: scheduleEditOptionsQueryKey(workspaceContext),
    queryFn: () => service.getScheduleEditOptions({ workspaceContext }),
    enabled
  });
}

export function usePrefetchAdjacentScheduleMonths(monthKey: string, enabled: boolean): void {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    for (const adjacentMonthKey of [
      getAdjacentScheduleMonth(monthKey, -1),
      getAdjacentScheduleMonth(monthKey, 1)
    ] as const) {
      const window = getScheduleMonthDateWindow(adjacentMonthKey);
      void queryClient
        .prefetchQuery({
          queryKey: scheduleMonthQueryKey(workspaceContext, adjacentMonthKey),
          queryFn: () => service.listSchedules({ workspaceContext, ...window })
        })
        .catch(() => undefined);
    }
  }, [enabled, monthKey, queryClient, service, workspaceContext]);
}

export function useContextMutationReset(): () => Promise<void> {
  const queryClient = useQueryClient();
  const resetController = useContextResetController();
  return useCallback(async () => {
    resetController.beginContextMutationReset();
    await queryClient.resetQueries(
      {
        queryKey: APP_BOOTSTRAP_QUERY_KEY,
        exact: true
      },
      {
        throwOnError: false
      }
    );
  }, [queryClient, resetController]);
}

export function assignmentListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['assignments', 'list', workspaceContext] as const;
}

export function assignmentDetailQueryKey(workspaceContext: WorkspaceContextValue, assignmentId: string) {
  return ['assignments', 'detail', workspaceContext, { assignmentId }] as const;
}

export function memberDisplayListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['members', 'displayList', workspaceContext] as const;
}

export function memberGroupSummaryListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['memberGroups', 'summaryList', workspaceContext] as const;
}

export function memberGroupDetailQueryKey(workspaceContext: WorkspaceContextValue, memberGroupId: string) {
  return ['memberGroups', 'detail', workspaceContext, { memberGroupId }] as const;
}

export function permissionGroupListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['permissionGroups', 'list', workspaceContext] as const;
}

export function permissionGroupDetailQueryKey(workspaceContext: WorkspaceContextValue, permissionGroupId: string) {
  return ['permissionGroups', 'detail', workspaceContext, { permissionGroupId }] as const;
}

export function memberManagementListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['members', 'managementList', workspaceContext] as const;
}

export function memberSelectorQueryKey(workspaceContext: WorkspaceContextValue, purpose: MemberSelectorPurpose) {
  return ['members', 'selector', workspaceContext, { purpose }] as const;
}

export function invitationListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['invitations', 'list', workspaceContext] as const;
}

export function approvalListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['approvals', 'list', workspaceContext] as const;
}

export function approvalDetailQueryKey(workspaceContext: WorkspaceContextValue, joinRequestId: string) {
  return ['approvals', 'detail', workspaceContext, { joinRequestId }] as const;
}

export function attendanceCategoryListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['attendanceCategories', 'list', workspaceContext] as const;
}

export function scheduleMonthQueryKey(workspaceContext: WorkspaceContextValue, monthKey: string) {
  return ['schedules', 'month', workspaceContext, { monthKey }] as const;
}

export function scheduleUpcomingQueryKey(workspaceContext: WorkspaceContextValue, limit: number) {
  return ['schedules', 'upcoming', workspaceContext, { limit }] as const;
}

export function scheduleDetailQueryKey(workspaceContext: WorkspaceContextValue, scheduleId: string) {
  return ['schedules', 'detail', workspaceContext, { scheduleId }] as const;
}

export function scheduleEditOptionsQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['schedules', 'editOptions', workspaceContext] as const;
}

export function scheduleHomeUpcomingQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['schedules', 'homeUpcoming', workspaceContext] as const;
}

export function resourcesRootQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['resources', 'root', workspaceContext] as const;
}

export function songListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['resources', 'songs', workspaceContext] as const;
}

export function songDetailQueryKey(workspaceContext: WorkspaceContextValue, songId: string, folderId: string | null) {
  return ['resources', 'songDetail', workspaceContext, { songId, folderId }] as const;
}

export function notificationListQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['notifications', 'list', workspaceContext] as const;
}

export function unreadNotificationStateQueryKey(workspaceContext: WorkspaceContextValue) {
  return ['notifications', 'unreadState', workspaceContext] as const;
}

export async function invalidateApprovalReviewQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  joinRequestId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: approvalListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: approvalDetailQueryKey(workspaceContext, joinRequestId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberDisplayListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberGroupSummaryListQueryKey(workspaceContext),
      exact: true
    })
  ]);
}

export async function invalidateScheduleMonths(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  monthKeys: readonly string[]
): Promise<void> {
  for (const monthKey of Array.from(new Set(monthKeys))) {
    await queryClient.invalidateQueries({
      queryKey: scheduleMonthQueryKey(workspaceContext, monthKey),
      exact: true
    });
  }
}

export async function invalidateScheduleUpcomingQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: ['schedules', 'upcoming', workspaceContext]
  });
}

export async function invalidateAttendanceCategoryManagementQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  options: { readonly affectsScheduleDisplayNames: boolean }
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: attendanceCategoryListQueryKey(workspaceContext),
    exact: true
  });
  await queryClient.invalidateQueries({
    queryKey: scheduleEditOptionsQueryKey(workspaceContext),
    exact: true
  });
  if (!options.affectsScheduleDisplayNames) {
    return;
  }

  await queryClient.invalidateQueries({
    predicate: (query) => isWorkspaceScheduleDisplayNameQuery(query.queryKey, workspaceContext)
  });
}

export async function invalidateMemberGroupCreateQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  input: {
    readonly memberGroupId: string;
    readonly affectsMembership: boolean;
  }
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: memberGroupSummaryListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberGroupDetailQueryKey(workspaceContext, input.memberGroupId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: scheduleEditOptionsQueryKey(workspaceContext),
      exact: true
    })
  ]);

  if (!input.affectsMembership) {
    return;
  }

  await invalidateMemberGroupMembershipAffectedQueries(queryClient, workspaceContext);
}

export async function invalidateMemberGroupStructureQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  memberGroupId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: memberGroupSummaryListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberGroupDetailQueryKey(workspaceContext, memberGroupId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: scheduleEditOptionsQueryKey(workspaceContext),
      exact: true
    })
  ]);
  await invalidateMemberGroupMembershipAffectedQueries(queryClient, workspaceContext);
}

export async function invalidateMemberGroupMembershipMutationQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  memberGroupId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: memberGroupDetailQueryKey(workspaceContext, memberGroupId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberGroupSummaryListQueryKey(workspaceContext),
      exact: true
    }),
    invalidateMemberGroupMembershipAffectedQueries(queryClient, workspaceContext)
  ]);
}

export async function invalidateMemberGroupManagerGrantQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  memberGroupId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: memberGroupDetailQueryKey(workspaceContext, memberGroupId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberGroupSummaryListQueryKey(workspaceContext),
      exact: true
    })
  ]);
}

export async function invalidateMemberGroupTagMutationQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  memberGroupId: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: memberGroupDetailQueryKey(workspaceContext, memberGroupId),
    exact: true
  });
}

export async function invalidatePermissionGroupStructureQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  permissionGroupId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: permissionGroupListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: permissionGroupDetailQueryKey(workspaceContext, permissionGroupId),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: approvalListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: APP_BOOTSTRAP_QUERY_KEY,
      exact: true
    })
  ]);
}

export async function invalidatePermissionGroupMemberMutationQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue,
  permissionGroupId: string
): Promise<void> {
  await Promise.all([
    invalidatePermissionGroupStructureQueries(queryClient, workspaceContext, permissionGroupId),
    queryClient.invalidateQueries({
      queryKey: memberSelectorQueryKey(workspaceContext, 'member_selection'),
      exact: true
    })
  ]);
}

export function resetContextWorkspaceState(queryClient: QueryClient): void {
  removeNonBootstrapQueries(queryClient);
}

function getScheduleMonthStaleTime(data: unknown): number {
  const refreshMs = getScheduleMonthRefetchInterval(data);
  return refreshMs === false ? 0 : refreshMs;
}

function getScheduleMonthRefetchInterval(data: unknown): number | false {
  return Array.isArray(data) ? getScheduleStatusBoundaryRefreshMs(data as readonly ScheduleListItem[]) : false;
}

async function invalidateMemberGroupMembershipAffectedQueries(
  queryClient: QueryClient,
  workspaceContext: WorkspaceContextValue
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: memberDisplayListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      queryKey: memberManagementListQueryKey(workspaceContext),
      exact: true
    }),
    queryClient.invalidateQueries({
      predicate: (query) => isWorkspaceMemberSelectorQuery(query.queryKey, workspaceContext)
    })
  ]);
}

function removeNonBootstrapQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => !isAppBootstrapQueryKey(query.queryKey)
  });
}

function isAppBootstrapQueryKey(queryKey: readonly unknown[]): boolean {
  return (
    queryKey.length === APP_BOOTSTRAP_QUERY_KEY.length &&
    queryKey.every((part, index) => part === APP_BOOTSTRAP_QUERY_KEY[index])
  );
}

function isWorkspaceMemberSelectorQuery(
  queryKey: readonly unknown[],
  workspaceContext: WorkspaceContextValue
): boolean {
  return (
    queryKey[0] === 'members' && queryKey[1] === 'selector' && isSameWorkspaceContext(queryKey[2], workspaceContext)
  );
}

function isWorkspaceScheduleDisplayNameQuery(
  queryKey: readonly unknown[],
  workspaceContext: WorkspaceContextValue
): boolean {
  if (queryKey[0] !== 'schedules') {
    return false;
  }
  if (!['month', 'upcoming', 'detail', 'homeUpcoming'].includes(String(queryKey[1]))) {
    return false;
  }
  return isSameWorkspaceContext(queryKey[2], workspaceContext);
}

function isSameWorkspaceContext(value: unknown, workspaceContext: WorkspaceContextValue): boolean {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    (value as WorkspaceContextValue).currentUserId === workspaceContext.currentUserId &&
    (value as WorkspaceContextValue).currentChoirId === workspaceContext.currentChoirId &&
    (value as WorkspaceContextValue).currentSeasonId === workspaceContext.currentSeasonId
  );
}
